import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  IconButton,
  Divider,
  Paper,
  LinearProgress,
  Alert,
  Avatar,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  Phone,
  WhatsApp,
  Email,
  LocationOn,
  AccountBalance,
  Person,
  CreditCard,
  Business,
  Work,
  CalendarToday,
  Assessment,
  Security,
  Tag,
  Edit,
  Refresh,
} from '@mui/icons-material';
import { getBorrowerById } from '../../services/request/Borrowers';
import { BorrowerAPIItem } from '../../models/LendingModels';
import { formatCurrency } from '../../utils/CurrencyFormat';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CustomerDetailsView: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const [borrower, setBorrower] = useState<BorrowerAPIItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (customerId) {
      fetchBorrowerDetails();
    }
  }, [customerId]);

  const fetchBorrowerDetails = async () => {
    if (!customerId) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await getBorrowerById(customerId);
      
      if (response) {
        setBorrower(response);
      } else {
        setError('Borrower not found');
      }
    } catch (err: any) {
      setError(err?.response?.status === 404 
        ? 'Borrower not found' 
        : `Failed to fetch borrower details: ${err.message || 'Unknown error'}`);
      console.error('Error fetching borrower:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status?.toUpperCase()) {
      case 'VERIFIED':
      case 'GOOD':
      case 'EXCELLENT':
      case 'LOW':
      case 'PREMIUM':
        return 'success';
      case 'PENDING':
      case 'AVERAGE':
      case 'MEDIUM':
      case 'STANDARD':
        return 'warning';
      case 'REJECTED':
      case 'BAD':
      case 'BLOCKED':
      case 'HIGH':
      case 'BASIC':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const maskSensitiveData = (value?: string, visibleChars: number = 4) => {
    if (!value) return 'N/A';
    if (value.length <= visibleChars) return value;
    return '****' + value.slice(-visibleChars);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading borrower details...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchBorrowerDetails}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin/customers')}
          sx={{ mt: 2 }}
        >
          Back to Customers
        </Button>
      </Box>
    );
  }

  if (!borrower) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No borrower data available</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <ToastContainer />
      
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/admin/customers')}>
            <ArrowBack />
          </IconButton>
          <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
            {borrower.name?.charAt(0).toUpperCase() || '?'}
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {borrower.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Borrower ID: {borrower.id.substring(0, 8)}...
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchBorrowerDetails}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={() => navigate(`/admin/customers/${customerId}/edit`)}
          >
            Edit
          </Button>
        </Stack>
      </Box>

      {/* Status Chips */}
      <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {borrower.creditStatus && (
          <Chip
            label={`Credit: ${borrower.creditStatus}`}
            color={getStatusColor(borrower.creditStatus)}
            size="small"
          />
        )}
        {borrower.kycStatus && (
          <Chip
            label={`KYC: ${borrower.kycStatus}`}
            color={getStatusColor(borrower.kycStatus)}
            size="small"
          />
        )}
        {borrower.riskCategory && (
          <Chip
            label={`Risk: ${borrower.riskCategory}`}
            color={getStatusColor(borrower.riskCategory)}
            size="small"
          />
        )}
        {borrower.customerSegment && (
          <Chip
            label={`Segment: ${borrower.customerSegment}`}
            color={getStatusColor(borrower.customerSegment)}
            size="small"
          />
        )}
        {borrower.totalOutstanding !== undefined && (
          <Chip
            label={`Outstanding: ${formatCurrency(borrower.totalOutstanding)}`}
            color="primary"
            size="small"
          />
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Phone sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  Contact Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {borrower.contactNumber && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Contact Number
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Phone fontSize="small" />
                      {borrower.contactNumber}
                    </Typography>
                  </Box>
                )}
                {borrower.whatsappNumber && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      WhatsApp Number
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WhatsApp fontSize="small" color="success" />
                      {borrower.whatsappNumber}
                      {borrower.isContactSameAsWhatsapp && (
                        <Chip label="Same as Contact" size="small" color="info" />
                      )}
                    </Typography>
                  </Box>
                )}
                {borrower.email && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Email fontSize="small" />
                      {borrower.email}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Address Information */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationOn sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  Address Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {borrower.address && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Address
                    </Typography>
                    <Typography variant="body1">
                      {borrower.address}
                    </Typography>
                  </Box>
                )}
                {(borrower.city || borrower.state || borrower.pincode) && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Location
                    </Typography>
                    <Typography variant="body1">
                      {[borrower.city, borrower.state, borrower.pincode]
                        .filter(Boolean)
                        .join(', ')}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Financial Information */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CreditCard sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  Financial Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {borrower.creditScore !== undefined && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Credit Score
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6" color={borrower.creditScore >= 750 ? 'success.main' : borrower.creditScore >= 650 ? 'warning.main' : 'error.main'}>
                        {borrower.creditScore}
                      </Typography>
                      <Chip
                        label={borrower.creditScore >= 750 ? 'Excellent' : borrower.creditScore >= 650 ? 'Good' : 'Fair'}
                        size="small"
                        color={borrower.creditScore >= 750 ? 'success' : borrower.creditScore >= 650 ? 'warning' : 'error'}
                      />
                    </Box>
                  </Box>
                )}
                {borrower.monthlyIncome !== undefined && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Monthly Income
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {formatCurrency(borrower.monthlyIncome)}
                    </Typography>
                  </Box>
                )}
                {borrower.totalOutstanding !== undefined && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total Outstanding
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="error.main">
                      {formatCurrency(borrower.totalOutstanding)}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Personal Information */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Person sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  Personal Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {borrower.dateOfBirth && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Date of Birth
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarToday fontSize="small" />
                      {formatDate(borrower.dateOfBirth)}
                    </Typography>
                  </Box>
                )}
                {borrower.panNumber && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      PAN Number
                    </Typography>
                    <Typography variant="body1">
                      {maskSensitiveData(borrower.panNumber, 4)}
                    </Typography>
                  </Box>
                )}
                {borrower.aadhaarNumber && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Aadhaar Number
                    </Typography>
                    <Typography variant="body1">
                      {maskSensitiveData(borrower.aadhaarNumber, 4)}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Employment Information */}
        {(borrower.employmentStatus || borrower.occupation || borrower.companyName) && (
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Work sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight="bold">
                    Employment Information
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  {borrower.employmentStatus && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Employment Status
                      </Typography>
                      <Chip
                        label={borrower.employmentStatus.replace('_', ' ')}
                        color={getStatusColor(borrower.employmentStatus)}
                        size="small"
                      />
                    </Box>
                  )}
                  {borrower.occupation && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Occupation
                      </Typography>
                      <Typography variant="body1">{borrower.occupation}</Typography>
                    </Box>
                  )}
                  {borrower.companyName && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Company Name
                      </Typography>
                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Business fontSize="small" />
                        {borrower.companyName}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Bank Information */}
        {(borrower.bankAccountNumber || borrower.ifscCode || borrower.bankName) && (
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AccountBalance sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight="bold">
                    Bank Information
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  {borrower.bankAccountNumber && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Account Number
                      </Typography>
                      <Typography variant="body1">
                        {maskSensitiveData(borrower.bankAccountNumber, 4)}
                      </Typography>
                    </Box>
                  )}
                  {borrower.ifscCode && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        IFSC Code
                      </Typography>
                      <Typography variant="body1">{borrower.ifscCode}</Typography>
                    </Box>
                  )}
                  {borrower.bankName && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Bank Name
                      </Typography>
                      <Typography variant="body1">{borrower.bankName}</Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* CRM Information */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Assessment sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  CRM Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {borrower.referralCode && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Referral Code
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary">
                      {borrower.referralCode}
                    </Typography>
                  </Box>
                )}
                {borrower.sourceOfLead && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Source of Lead
                    </Typography>
                    <Chip
                      label={borrower.sourceOfLead.replace('_', ' ')}
                      size="small"
                      color="primary"
                    />
                  </Box>
                )}
                {borrower.lastContactDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Last Contact Date
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarToday fontSize="small" />
                      {formatDate(borrower.lastContactDate)}
                    </Typography>
                  </Box>
                )}
                {borrower.nextFollowupDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Next Follow-up Date
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarToday fontSize="small" />
                      {formatDate(borrower.nextFollowupDate)}
                    </Typography>
                  </Box>
                )}
                {borrower.tags && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Tags
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                      {borrower.tags.split(',').map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag.trim()}
                          size="small"
                          icon={<Tag />}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Additional Information */}
        {borrower.comments && (
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Security sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight="bold">
                    Additional Information
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1">{borrower.comments}</Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Action Buttons */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={() => navigate(`/admin/customers/${customerId}/debits`)}
        >
          View Debits
        </Button>
        <Button
          variant="contained"
          onClick={() => navigate(`/admin/customers/${customerId}/debits`)}
        >
          Manage Loans
        </Button>
      </Box>
    </Box>
  );
};

export default CustomerDetailsView;
